/*
 * Cart clarity guard for concert presales.
 *
 * This deliberately does not prevent checkout. Shopify pickup availability
 * remains the enforcement layer; this script tells the shopper which cart
 * line is incompatible before they reach Shopify's generic pickup message.
 */
(() => {
  const SHOP_DOMAIN = 'lv-ciudvd.myshopify.com';
  const ENDPOINT = 'https://multiloca.info/api/get-collection-product-inventory';
  const inventoryCache = new Map();
  let renderRevision = 0;
  let scheduled = false;

  const normalizeId = (value) => {
    if (value == null) return null;
    if (typeof value === 'object') value = value.locationId ?? value.location_id ?? value.id ?? value.value ?? value.location?.id ?? null;
    const string = String(value ?? '').trim();
    const gid = string.match(/gid:\/\/shopify\/Location\/(\d+)/i);
    return gid ? gid[1] : (/^\d+$/.test(string) ? string : null);
  };

  function selectedLocation() {
    const radio = document.querySelector('#locationNamesContainer input[type="radio"]:checked');
    const wrapper = radio?.closest('.location-radio-and-label, .location-container');
    const id = [radio?.dataset.locationId, radio?.getAttribute('data-location-id'), radio?.value, wrapper?.dataset.locationId, wrapper?.getAttribute('data-location-id')]
      .map(normalizeId).find(Boolean);
    if (id) return id;
    try {
      const raw = localStorage.getItem('selected_location');
      return raw ? normalizeId(JSON.parse(raw)) || normalizeId(raw) : null;
    } catch { return null; }
  }

  function selectedShowName() {
    const radio = document.querySelector('#locationNamesContainer input[type="radio"]:checked');
    const label = radio?.closest('label') || radio?.closest('.location-radio-and-label, .location-container')?.querySelector('label');
    const name = (label?.textContent || radio?.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim();
    return name || 'Selected show';
  }

  async function inventory(productId) {
    if (!inventoryCache.has(productId)) {
      const url = new URL(ENDPOINT);
      url.searchParams.set('shopdomain', SHOP_DOMAIN);
      url.searchParams.set('productId', productId);
      inventoryCache.set(productId, fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`Inventory API ${response.status}`);
        return response.json();
      }).catch((error) => { inventoryCache.delete(productId); throw error; }));
    }
    return inventoryCache.get(productId);
  }

  function availableAtLocation(payload, variantId, locationId) {
    const map = payload?.inventoryMap || {};
    const entry = map[String(variantId)] || map[variantId];
    const level = (entry?.inventoryLevels || []).find((item) => normalizeId(item.location_id ?? item.locationId ?? item.location?.id) === locationId);
    return Boolean(level && Number(level.available_quantity ?? level.availableQuantity ?? 0) > 0);
  }

  function clearUI() {
    document.querySelectorAll('[data-presale-cart-label], [data-presale-cart-warning]').forEach((node) => node.remove());
  }

  function addLineLabel(item, showName) {
    const row = [...document.querySelectorAll('[data-key]')].find((node) => node.dataset.key === item.key);
    const target = row?.querySelector('.cart-items__variants-wrapper, .cart-items__product-info');
    if (!target) return;
    const label = document.createElement('p');
    label.className = 'presale-cart-label';
    label.dataset.presaleCartLabel = 'true';
    label.textContent = `Pickup show: ${showName}`;
    target.append(label);
  }

  function addWarning(incompatible, showName) {
    if (!incompatible.length) return;
    const target = document.querySelector('.cart__ctas') || document.querySelector('.cart-summary') || document.querySelector('cart-items-component');
    if (!target) return;
    const warning = document.createElement('section');
    warning.className = 'presale-cart-warning';
    warning.dataset.presaleCartWarning = 'true';
    warning.setAttribute('role', 'alert');
    const list = incompatible.map((item) => `<li>${escapeHTML(item.product_title)}${item.variant_title && item.variant_title !== 'Default Title' ? ` — ${escapeHTML(item.variant_title)}` : ''}</li>`).join('');
    warning.innerHTML = `<strong>Items not available for ${escapeHTML(showName)}</strong><p>Remove or replace these items before pickup checkout:</p><ul>${list}</ul><p>Shopify will not offer a pickup location until the cart is corrected.</p>`;
    target.before(warning);
  }

  function escapeHTML(value) {
    const node = document.createElement('span');
    node.textContent = value;
    return node.innerHTML;
  }

  async function render() {
    scheduled = false;
    const revision = ++renderRevision;
    clearUI();
    const locationId = selectedLocation();
    if (!locationId) return;
    try {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(`Cart API ${cartResponse.status}`);
      const cart = await cartResponse.json();
      const products = [...new Set(cart.items.map((item) => item.product_id))];
      const productInventory = new Map(await Promise.all(products.map(async (id) => [id, await inventory(id)])));
      if (revision !== renderRevision) return;
      const showName = selectedShowName();
      const incompatible = [];
      cart.items.forEach((item) => {
        addLineLabel(item, showName);
        if (!availableAtLocation(productInventory.get(item.product_id), item.variant_id, locationId)) incompatible.push(item);
      });
      addWarning(incompatible, showName);
    } catch (error) {
      console.warn('[Presale Cart]', error);
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(render, 50);
  }

  document.addEventListener('locationChanged', schedule);
  document.addEventListener('presale:cart-context', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('cart:update', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
})();
